import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Button, DialogActions, DialogContent, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { TFormType, TPartnerFormData } from 'src/@types';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import FormProvider, { RHFSelect, RHFSwitch, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import FileUpload from 'src/components/upload/FileUpload';
import {
  FormInnerContainer,
  FormInnerContainerRow,
  FormItem,
  FormLabel,
  FormRow,
} from 'src/layouts/form/FormLayout';
import { IPartner, useStores } from 'src/models';
import { partnerApi } from 'src/service';
import { useHandleResponse } from 'src/service/useHandleResponse';
import { FormType } from 'src/static/constants/common/form';
import { getFileUrl, separateFilesAndUrls } from 'src/utils/fileUploadUtils';
import { getPartnerFormDefaultValues } from 'src/utils/getRHFDefaultValues';

interface IPartnerFormDialogProps {
  formType: TFormType;
  onSubmitSuccess: () => void;
  selectedData?: IPartner;
}

export const PartnerFormDialog = observer(
  ({ formType, onSubmitSuccess, selectedData }: IPartnerFormDialogProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const handleResponse = useHandleResponse();
    const { alertStore, commonCodeStore } = useStores();
    const { formId } = useDialogContext();
    const readonly = formType === FormType.READONLY;
    const isCreate = formType === FormType.CREATE;
    const [file, setFile] = useState<Array<File | string>>([]);
    const methods = useForm<TPartnerFormData>({
      mode: 'all',
      reValidateMode: 'onChange',
      defaultValues: getPartnerFormDefaultValues(selectedData),
    });

    useEffect(() => {
      setFile(selectedData?.logoImgPath ? [selectedData.logoImgPath] : []);
    }, []);

    const PARTNER_CTGY_CD = commonCodeStore.getDynamicCodes(500005);

    // 등록 / 수정 함수
    const onValid = async (data: TPartnerFormData) => {
      const { uploadedFiles, paths } = separateFilesAndUrls(file);

      let formData: TPartnerFormData = {
        ...(uploadedFiles.length > 0 && { file: uploadedFiles[0] }),
        ...data,
      };

      if (!isCreate) {
        formData = {
          ...formData,
          partnerSid: selectedData?.partnerSid,
          ...(uploadedFiles.length > 0 && { file: uploadedFiles[0] }),
          // ...(paths.length > 0 && { logoImgPath: paths }),
          logoImgPath: paths,
        };
      }

      const result = isCreate ? partnerApi.post(formData) : partnerApi.put(formData);
      if (await result) {
        handleResponse('등록이 완료되었습니다.', '등록이 실패되었습니다.', onSubmitSuccess);
      }
    };

    // 삭제
    const handleDelete = () => {
      alertStore.setOnConfirm(async () => {
        if (selectedData?.partnerSid) {
          partnerApi
            .remove(selectedData.partnerSid)
            .then(() =>
              handleResponse('삭제가 완료되었습니다.', '삭제가 실패되었습니다.', onSubmitSuccess),
            );
        }
      });
      alertStore.setProps({
        open: true,
        title: '파트너 삭제',
        content: '해당 파트너를 삭제하시겠습니까?',
        category: 'error',
        hasCancelButton: true,
      });
    };

    return (
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onValid)} formId={formId}>
        <DialogContent dividers sx={{ pt: '10px' }}>
          <FormInnerContainerRow>
            <FormInnerContainer sx={{ width: '100%' }}>
              <FormLabel>{t('common.form.section.basicInfo')}</FormLabel>
              <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                <FormItem>
                  <RHFSwitch label={t('partner.form.useYn')} name="useYn" disabled={readonly} />
                </FormItem>
              </FormRow>
              <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                <FormItem>
                  <RHFSelect
                    label={t('partner.form.categoryType')}
                    name="categoryType"
                    options={[]}
                    disabled={readonly}
                    useAll={false}
                  />
                </FormItem>
              </FormRow>
              <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                <FormItem>
                  <RHFTextField
                    label={t('partner.form.partnerName')}
                    name="partnerName"
                    disabled={readonly}
                  />
                </FormItem>
              </FormRow>
            </FormInnerContainer>
            <FormInnerContainer>
              <FormLabel>{t('partner.form.logoImage')}</FormLabel>
              <Box sx={{ height: '100%' }}>
                {file.length > 0 ? (
                  <Box
                    sx={{
                      position: 'relative',
                      padding: 1,
                      borderRadius: 1,
                    }}
                  >
                    <img
                      width={'100%'}
                      src={getFileUrl(file[0])}
                      style={{ maxWidth: 300, minWidth: 200, maxHeight: 170 }}
                      alt="logo-image"
                    />
                    <Button
                      onClick={() => setFile([])}
                      sx={{
                        cursor: 'pointer',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: 30,
                        minWidth: 30,
                        height: 30,
                        backgroundColor: theme.palette.grey[700],
                        borderRadius: 1,
                        padding: '3px',
                        opacity: 0.8,
                        '&:hover': { opacity: 1, backgroundColor: theme.palette.grey[900] },
                      }}
                    >
                      <Iconify icon="eva:close-fill" width={20} color={theme.palette.grey[300]} />
                    </Button>
                  </Box>
                ) : (
                  <div style={{ height: '100%', minHeight: 170 }}>
                    <FileUpload
                      files={[]}
                      showPreview
                      showFileIcon={false}
                      maxFiles={1}
                      onDrop={(newFile) => setFile(newFile)}
                      accept={{ 'image/jpeg': [], 'image/jpg': [], 'image/png': [] }}
                    />
                  </div>
                )}
              </Box>
            </FormInnerContainer>
          </FormInnerContainerRow>
        </DialogContent>
        <DialogActions>
          <>
            {!isCreate && (
              <Button onClick={handleDelete} variant="contained" color="error">
                {t('common.button.delete')}
              </Button>
            )}
            <Button type="submit" variant="contained" form={formId} color="primary">
              {isCreate ? t('common.button.register') : t('common.button.edit')}
            </Button>
          </>
        </DialogActions>
      </FormProvider>
    );
  },
);
